import { useState, useEffect, useCallback } from 'react'
import { EnvironmentConfig, ConfigValidationResult, UseEnvironmentConfigReturn } from '../types/EnvironmentConfig.types'
import { defaultEnvironmentConfig } from '../utils/defaultConfigs'
import { ServiceFactory } from '../../../../../../core/factories/ServiceFactory'
import { waitlistUrlInputError } from '../../../../../../utils/signupWaitlist'
import { mapEnvironmentConfigToWorkspaceSettings, mapWorkspaceSettingsToEnvironmentConfig } from '../utils/workspaceMapper'
import { useAppDispatch } from '../../../../../../hooks'
import { setSettings } from '../../../../../../stores/WorkspaceStore'
import type { WorkspaceSettings } from '../../../../../../core/models/Workspace'

// The settings chain never fails loudly: on errors and timeouts it hands back null or a stand-in
// (WorkspaceService.getDefaultSettings, public/workspace-settings.json,
// DEFAULT_WORKSPACE_SETTINGS), and every stand-in has id 'default'. Saving from one would write its
// defaults - including an open Registration Mode - over the stored row, so only a real record
// unlocks Save. Cloud marks its own "no row yet" reply with `stored: false`: there is nothing to
// overwrite then, and saving creates the row.
const isNoSavedSettings = (settings: WorkspaceSettings | null): boolean =>
  !!settings && settings.stored === false

const isLoadedWorkspaceSettings = (settings: WorkspaceSettings | null): boolean =>
  isNoSavedSettings(settings) || (!!settings && !!settings.id && settings.id !== 'default')

const sameList = (a: string[], b: string[]): boolean => JSON.stringify(a) === JSON.stringify(b)

/**
 * Custom hook for managing environment configuration
 * Handles loading, saving, validation, and state management
 */
export const useEnvironmentConfig = (): UseEnvironmentConfigReturn => {
  const dispatch = useAppDispatch()
  const [config, setConfig] = useState<EnvironmentConfig>(defaultEnvironmentConfig)
  const [originalConfig, setOriginalConfig] = useState<EnvironmentConfig>(defaultEnvironmentConfig)
  const [loading, setLoading] = useState(true)
  const [settingsLoaded, setSettingsLoaded] = useState(false)
  const [noSavedSettings, setNoSavedSettings] = useState(false)
  const [blockedDomainsShown, setBlockedDomainsShown] = useState(true)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<any[]>([])
  const [warnings, setWarnings] = useState<any[]>([])

  // Load configuration on mount
  useEffect(() => {
    loadConfig()
  }, [])

  // Load configuration from storage or API
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true)
      setSettingsLoaded(false)
      setNoSavedSettings(false)
      setBlockedDomainsShown(true)
      
      // Get workspace service and load settings
      const workspaceService = ServiceFactory.getInstance().getWorkspaceService()
      // Re-read instead of trusting the copy cached at app start: another admin or tab may have
      // created or changed the row since, and saving that stale copy would overwrite it. `fresh`
      // also skips cloud's per-worker cache, which a save handled by another worker leaves stale.
      await workspaceService.loadSettings({ fresh: true })
      const workspaceSettings = await workspaceService.getSettings()
      setSettingsLoaded(isLoadedWorkspaceSettings(workspaceSettings))
      setNoSavedSettings(isNoSavedSettings(workspaceSettings))
      // The public settings read leaves blocked_domains out, so an empty list here can mean
      // "not shown" rather than "none blocked".
      setBlockedDomainsShown(
        isNoSavedSettings(workspaceSettings) || Array.isArray(workspaceSettings?.blocked_domains)
      )
      
      if (workspaceSettings) {
        // Map workspace settings to environment config format
        const mappedConfig = mapWorkspaceSettingsToEnvironmentConfig(workspaceSettings)
        const loadedConfig = {
          ...defaultEnvironmentConfig,
          ...mappedConfig
        }
        setConfig(loadedConfig)
        setOriginalConfig(loadedConfig)
      } else {
        // Use default configuration if no settings found
        setConfig(defaultEnvironmentConfig)
        setOriginalConfig(defaultEnvironmentConfig)
      }
    } catch (error) {
      console.error('Failed to load configuration:', error)
      // Use default configuration as fallback
      setConfig(defaultEnvironmentConfig)
      setOriginalConfig(defaultEnvironmentConfig)
    } finally {
      setLoading(false)
    }
  }, [])

  // Update configuration
  const updateConfig = useCallback((updates: Partial<EnvironmentConfig>) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      ...updates
    }))
  }, [])

  // Save configuration
  const saveConfig = useCallback(async () => {
    if (!settingsLoaded) {
      throw new Error('Workspace settings were not loaded; refusing to overwrite them')
    }
    try {
      setSaving(true)

      // Cloud refuses a non-https Waitlist URL, so stop here: the field already shows the
      // reason, which is clearer than cloud's refusal. [SC-443]
      const waitlistError = waitlistUrlInputError(config.registration.waitlistUrl)
      if (waitlistError) {
        throw new Error(`Waitlist URL: ${waitlistError}`)
      }

      console.log('[useEnvironmentConfig] Saving configuration:', config)
      
      // Get workspace service and save settings
      const workspaceService = ServiceFactory.getInstance().getWorkspaceService()
      
      // Map environment config to workspace settings format
      const workspaceSettings = mapEnvironmentConfigToWorkspaceSettings(config)
      // Cloud keeps any field the save leaves out. A list this screen never saw is left out
      // unless the admin typed a new one, so saving something else can't wipe it.
      const blockedEdited = !sameList(
        config.registration.emailDomains.blocked,
        originalConfig.registration.emailDomains.blocked
      )
      if (!blockedDomainsShown && !blockedEdited) {
        delete workspaceSettings.blocked_domains
      }
      console.log('[useEnvironmentConfig] Mapped to workspace settings:', workspaceSettings)
      
      // Update settings via workspace service - pass the settings object
      const updatedSettings = await workspaceService.updateSettings(workspaceSettings)
      console.log('[useEnvironmentConfig] Settings saved, received:', updatedSettings)
      if (!updatedSettings) {
        throw new Error('Failed to update workspace settings')
      }
      
      // Update Redux store with new settings so AuthenticationDialog sees changes immediately
      console.log('[useEnvironmentConfig] Updating Redux store with settings:', updatedSettings)
      dispatch(setSettings(updatedSettings))
      
      setOriginalConfig(config)
      setNoSavedSettings(false)
      // A list that was just saved is the stored list, so the form now shows it.
      if ('blocked_domains' in workspaceSettings) setBlockedDomainsShown(true)
      setErrors([])
      setWarnings([])
    } catch (error) {
      console.error('Failed to save configuration:', error)
      throw error
    } finally {
      setSaving(false)
    }
  }, [config, originalConfig, dispatch, settingsLoaded, blockedDomainsShown])

  // Reset configuration to defaults
  const resetConfig = useCallback(() => {
    setConfig(defaultEnvironmentConfig)
    setErrors([])
    setWarnings([])
  }, [])

  // Validate configuration
  const validateConfig = useCallback((): ConfigValidationResult => {
    const newErrors: any[] = []
    const newWarnings: any[] = []

    // Platform validation
    if (!config.branding.platformName?.trim()) {
      newErrors.push({
        field: 'branding.platformName',
        message: 'Platform name is required',
        code: 'REQUIRED_FIELD'
      })
    }

    if (!config.branding.platformUrl?.trim()) {
      newErrors.push({
        field: 'branding.platformUrl',
        message: 'Platform URL is required',
        code: 'REQUIRED_FIELD'
      })
    }

    // Security validation
    if (config.security.password.minLength < 6) {
      newWarnings.push({
        field: 'security.password.minLength',
        message: 'Minimum password length should be at least 6 characters',
        code: 'WEAK_PASSWORD'
      })
    }

    if (config.security.session.timeout < 5) {
      newWarnings.push({
        field: 'security.session.timeout',
        message: 'Session timeout should be at least 5 minutes',
        code: 'SHORT_SESSION'
      })
    }

    // Registration validation
    if (config.registration.limits.maxUsers < 1) {
      newErrors.push({
        field: 'registration.limits.maxUsers',
        message: 'Maximum users must be at least 1',
        code: 'INVALID_LIMIT'
      })
    }

    if (config.registration.limits.maxRooms < 1) {
      newErrors.push({
        field: 'registration.limits.maxRooms',
        message: 'Maximum rooms must be at least 1',
        code: 'INVALID_LIMIT'
      })
    }

    // Integration validation
    if (config.integrations.email.provider === 'smtp') {
      const smtp = config.integrations.email.smtp
      if (!smtp?.host?.trim()) {
        newErrors.push({
          field: 'integrations.email.smtp.host',
          message: 'SMTP host is required when using SMTP provider',
          code: 'REQUIRED_FIELD'
        })
      }

      if (!smtp?.port || smtp.port < 1 || smtp.port > 65535) {
        newErrors.push({
          field: 'integrations.email.smtp.port',
          message: 'SMTP port must be between 1 and 65535',
          code: 'INVALID_PORT'
        })
      }
    }

    // Advanced validation
    if (config.advanced.performance.caching.ttl < 60) {
      newWarnings.push({
        field: 'advanced.performance.caching.ttl',
        message: 'Cache TTL should be at least 60 seconds',
        code: 'SHORT_CACHE'
      })
    }

    return {
      isValid: newErrors.length === 0,
      errors: newErrors,
      warnings: newWarnings
    }
  }, [config])

  // Check if there are unsaved changes
  const hasChanges = JSON.stringify(config) !== JSON.stringify(originalConfig)

  return {
    config,
    loading,
    settingsLoaded,
    noSavedSettings,
    blockedDomainsShown,
    saving,
    errors,
    warnings,
    hasChanges,
    updateConfig,
    saveConfig,
    resetConfig,
    validateConfig,
    loadConfig
  }
} 